import { BarChart3, Bot, Globe2, ShieldCheck, TrendingUp, Users } from "lucide-react";

const ICONS = [TrendingUp, Users, ShieldCheck, Bot, Globe2, BarChart3];
const STAT_RE = /~?\d[\d,.]*%?\+?/;
const SPARK = "M0 40 L20 36 L40 38 L60 28 L80 30 L100 18 L120 21 L140 9 L156 5";

function Sparkline() {
  return (
    <svg viewBox="0 0 160 48" className="w-36 h-11" aria-hidden>
      <path d={`${SPARK} L156 48 L0 48 Z`} fill="url(#cws-spark-fill)" />
      <path d={SPARK} fill="none" stroke="url(#cws-av)" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
      <circle cx="156" cy="5" r="3.5" fill="#F59E0B" className="animate-pulse" />
    </svg>
  );
}

function Ring({ pct }) {
  const r = 22;
  const c = 2 * Math.PI * r;
  return (
    <svg viewBox="0 0 56 56" className="w-14 h-14 -rotate-90" aria-hidden>
      <circle cx="28" cy="28" r={r} fill="none" stroke="#27272a" strokeWidth="5" />
      <circle cx="28" cy="28" r={r} fill="none" stroke="url(#cws-av)" strokeWidth="5" strokeLinecap="round" strokeDasharray={c} strokeDashoffset={c * (1 - Math.min(pct, 100) / 100)} />
    </svg>
  );
}

function Bars() {
  const hs = [12, 20, 16, 28, 36];
  return (
    <svg viewBox="0 0 56 40" className="w-14 h-10" aria-hidden>
      {hs.map((h, i) => <rect key={i} x={i * 11.5} y={40 - h} width="8" height={h} rx="2" fill={i === hs.length - 1 ? "#F59E0B" : "#3f3f46"} />)}
    </svg>
  );
}

function StatCard({ text, i }) {
  const Icon = ICONS[i % ICONS.length];
  const wide = i % 6 === 0 || i % 6 === 5;
  const stat = (text.match(STAT_RE) || [""])[0].trim();
  const pct = stat.includes("%") ? parseFloat(stat.replace(/[^\d.]/g, "")) : null;
  return (
    <div data-testid={`achievement-${i}`} className={`group relative overflow-hidden rounded-2xl border border-zinc-800 bg-zinc-950/60 p-6 transition-[transform,border-color,box-shadow] duration-300 hover:-translate-y-1 hover:border-amber-500/40 hover:shadow-[0_24px_60px_-30px_rgba(245,158,11,0.6)] ${wide ? "md:col-span-2" : ""}`}>
      <div aria-hidden className="absolute -top-16 -right-16 w-44 h-44 rounded-full bg-amber-500/10 blur-3xl opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
      <div aria-hidden className="absolute top-0 left-6 h-px w-20 bg-gradient-to-r from-amber-500 to-violet-500" />
      <div className="relative flex items-start justify-between gap-4">
        <span className="w-10 h-10 rounded-xl bg-amber-500/10 border border-amber-500/30 text-amber-400 flex items-center justify-center transition-transform duration-300 group-hover:scale-110">
          <Icon className="w-5 h-5" />
        </span>
        {wide ? <Sparkline /> : pct !== null ? <Ring pct={pct} /> : <Bars />}
      </div>
      {stat && (
        <div data-testid={`achievement-stat-${i}`} className="relative mt-5 font-adult-mono text-4xl md:text-5xl font-semibold tracking-tight bg-gradient-to-r from-amber-200 via-amber-400 to-amber-500 bg-clip-text text-transparent">{stat}</div>
      )}
      <p className="relative mt-2 text-sm leading-relaxed text-zinc-300">{text}</p>
    </div>
  );
}

export function NumbersBento({ items = [] }) {
  return (
    <section data-testid="founder-numbers" className="max-w-7xl mx-auto px-6 py-10">
      <svg width="0" height="0" className="absolute" aria-hidden>
        <defs>
          <linearGradient id="cws-av" x1="0" y1="0" x2="1" y2="0"><stop offset="0" stopColor="#F59E0B" /><stop offset="1" stopColor="#8B5CF6" /></linearGradient>
          <linearGradient id="cws-spark-fill" x1="0" y1="0" x2="0" y2="1"><stop offset="0" stopColor="#F59E0B" stopOpacity="0.28" /><stop offset="1" stopColor="#F59E0B" stopOpacity="0" /></linearGradient>
        </defs>
      </svg>
      <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
        <div>
          <div className="text-[11px] uppercase tracking-[0.28em] text-zinc-500 font-adult-mono code-underline">Numbers that matter</div>
          <h2 className="mt-4 font-adult-heading text-3xl md:text-4xl font-semibold tracking-tight">A track record in <span className="font-adult-serif italic text-amber-400">numbers.</span></h2>
        </div>
      </div>
      <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
        {items.map((a, i) => <StatCard key={i} text={a} i={i} />)}
      </div>
    </section>
  );
}
